import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import { setUser, setLoading as setAuthLoading } from '../store/slices/authSlice';
import {
  setGithubConnected,
  setGithubStatus,
  setCommitSettings,
  setStreakProgress,
  setGithubLoading,
} from '../store/slices/githubSlice';
// Convex hooks — RULES OF HOOKS COMPLIANT (no try-catch wrappers around hook calls)
import { useConvexGitHub, useConvexCommitSettings, useConvexDashboard } from './useConvexGitHub';
import useConvexAuth from './useConvexAuth';

/**
 * Custom hook for managing application-wide data loading.
 * RULES OF HOOKS: All hooks called unconditionally at top level.
 */
export const useAppData = () => {
  const dispatch    = useDispatch();
  const { user, isSignedIn } = useUser();
  const loadingRef  = useRef(false);

  // ✅ Hooks called unconditionally — NO try-catch around these
  const { githubStatus: convexGithubStatus }     = useConvexGitHub();
  const { commitSettings: convexCommitSettings } = useConvexCommitSettings();
  const { streakProgress: convexStreakProgress } = useConvexDashboard();
  const { userProfile: convexUserProfile }       = useConvexAuth();

  // ---------------------------------------------------------------
  // loadGithubStatus
  // ---------------------------------------------------------------
  const loadGithubStatus = useCallback(async () => {
    dispatch(setGithubLoading(true));
    try {
      if (convexGithubStatus && !convexGithubStatus.loading) {
        dispatch(setGithubConnected({
          connected:      convexGithubStatus.connected,
          username:       convexGithubStatus.githubUsername,
          avatar_url:     convexGithubStatus.avatarUrl,
          last_validated: convexGithubStatus.lastValidated,
          message:        convexGithubStatus.message,
        }));
      }
    } catch (error) {
      console.error('❌ Error loading GitHub status:', error);
    } finally {
      dispatch(setGithubLoading(false));
    }
  }, [convexGithubStatus, dispatch]);

  // ---------------------------------------------------------------
  // loadUserData
  // ---------------------------------------------------------------
  const loadUserData = useCallback(async () => {
    if (!isSignedIn) { dispatch(setAuthLoading(false)); return; }
    if (loadingRef.current) return;
    loadingRef.current = true;
    dispatch(setAuthLoading(true));

    const forceTimeout = setTimeout(() => {
      dispatch(setAuthLoading(false));
      loadingRef.current = false;
    }, 2000);

    try {
      if (user) dispatch(setUser(user));
      dispatch(setAuthLoading(false));
      loadingRef.current = false;
      clearTimeout(forceTimeout);

      Promise.allSettled([
        Promise.resolve().then(() => {
          if (convexUserProfile?.commit_settings) dispatch(setCommitSettings(convexUserProfile.commit_settings));
        }),
        Promise.resolve().then(() => {
          if (convexGithubStatus && !convexGithubStatus.loading) {
            dispatch(setGithubStatus({
              connected:            convexGithubStatus.connected,
              username:             convexGithubStatus.githubUsername,
              avatar_url:           convexGithubStatus.avatarUrl,
              last_validated:       convexGithubStatus.lastValidated,
              message:              convexGithubStatus.message,
              subscription_required: false,
            }));
          }
        }),
        Promise.resolve().then(() => {
          if (convexCommitSettings && !convexCommitSettings.loading) dispatch(setCommitSettings(convexCommitSettings));
        }),
        Promise.resolve().then(() => {
          if (convexStreakProgress && !convexStreakProgress.loading) dispatch(setStreakProgress(convexStreakProgress));
        }),
      ]);
    } catch (error) {
      console.error('Load user error:', error);
      dispatch(setAuthLoading(false));
      loadingRef.current = false;
      clearTimeout(forceTimeout);
    }
  }, [isSignedIn, user?.id, convexGithubStatus, convexCommitSettings, convexStreakProgress, convexUserProfile, dispatch]);

  // ---------------------------------------------------------------
  // syncGithubStatus
  // ---------------------------------------------------------------
  const syncGithubStatus = useCallback(() => {
    dispatch(setGithubLoading(true));
    try {
      if (convexGithubStatus && !convexGithubStatus.loading) {
        dispatch(setGithubStatus({
          connected:            convexGithubStatus.connected,
          username:             convexGithubStatus.githubUsername,
          avatar_url:           convexGithubStatus.avatarUrl,
          last_validated:       convexGithubStatus.lastValidated,
          message:              convexGithubStatus.message,
          subscription_required: false,
        }));
      }
    } catch (error) {
      console.error('GitHub status sync error:', error);
    } finally {
      dispatch(setGithubLoading(false));
    }
  }, [convexGithubStatus, dispatch]);

  // ---------------------------------------------------------------
  // syncStreakProgress
  // ---------------------------------------------------------------
  const syncStreakProgress = useCallback(() => {
    try {
      if (convexStreakProgress && !convexStreakProgress.loading) {
        dispatch(setStreakProgress(convexStreakProgress));
        return convexStreakProgress;
      }
    } catch (error) {
      console.error('Streak progress sync error:', error);
    }
    return null;
  }, [dispatch, convexStreakProgress]);

  // Deprecated stubs
  const loadSubscriptionStatus = useCallback(async () => {
    console.warn('⚠️ DEPRECATED: loadSubscriptionStatus() — use useConvexSubscriptionSync hook instead.');
  }, []);

  const refreshAllData = useCallback(async () => {
    syncGithubStatus();
    syncStreakProgress();
  }, [syncGithubStatus, syncStreakProgress]);

  const loadStreakProgress = useCallback(async () => {
    return syncStreakProgress();
  }, [syncStreakProgress]);

  return {
    loadUserData,
    refreshAllData,
    syncGithubStatus,
    syncStreakProgress,
    loadGithubStatus,
    loadStreakProgress,
    loadSubscriptionStatus,
  };
};

export default useAppData;
